import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloudUpload, FileCheck2, GraduationCap, Eye } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

const daysUntil = (iso) => (iso ? Math.ceil((new Date(iso) - Date.now()) / 86400000) : null);

const openFile = (c) => {
  const isPdf = (c.file_name || "").toLowerCase().endsWith(".pdf");
  const bytes = Uint8Array.from(atob(String(c.file_base64).split(",").pop()), (x) => x.charCodeAt(0));
  window.open(URL.createObjectURL(new Blob([bytes], { type: isPdf ? "application/pdf" : "image/png" })), "_blank");
};

export default function MyLsoCert() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [certs, setCerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => api("/certificates/me").then((r) => setCerts(r || [])).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g|pdf)$/i.test(file.name)) return setErr("Please upload a PNG, JPG, or PDF.");
    if (file.size > 4.5 * 1024 * 1024) return setErr("Maximum size is ~4.5MB. Try a smaller scan or a JPG.");
    setBusy(true); setErr(null);
    try {
      const base64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(",")[1]); fr.onerror = rej; fr.readAsDataURL(file); });
      const res = await api("/certificates/self", { method: "POST", body: { file_base64: base64, file_name: file.name } });
      toast.success(res?.expires_at ? `Certificate uploaded — valid until ${fmtDate(res.expires_at)}.` : "Certificate uploaded.");
      await load();
      refreshUser();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  const latest = certs?.[0];
  const days = daysUntil(latest?.expires_at);

  return (
    <PortalPage eyebrow="LASER SAFETY OFFICER" title="My LSO Training" testId="my-lso-cert-page">
      {!certs && !err && <Spinner />}
      <ErrorNote>{err}</ErrorNote>
      {certs && (latest ? (
        <Card style={{ borderColor: days != null && days <= 0 ? "#FF3333" : "var(--accent)" }} data-testid="my-lso-latest">
          <FileCheck2 size={32} className="text-[var(--accent)]" />
          <p className="mt-3 font-display text-2xl font-bold uppercase">Certificate on file</p>
          <p className="mt-1 text-sm text-neutral-300">{latest.file_name}</p>
          <div className="mt-3 space-y-1 text-sm text-neutral-400">
            <p>{latest.source === "lso_training" ? "Issued" : "Uploaded"}: {fmtDate(latest.uploaded_at)}</p>
            {latest.lso_cert_number && <p>Certificate #: <span className="font-mono text-white">{latest.lso_cert_number}</span></p>}
            {latest.expires_at && <p data-testid="my-lso-expiry">Expires: {fmtDate(latest.expires_at)} {days != null && (days > 0 ? `(${days} days left)` : "(expired)")}</p>}
            <p className="text-xs">{latest.source === "lso_training" ? "Earned through ALR LSO Training" : latest.source === "admin_upload" ? "Uploaded by ALR" : "Self-uploaded"}</p>
          </div>
          {latest.file_base64 && <Btn variant="outline" className="mt-4" onClick={() => openFile(latest)} data-testid="my-lso-view"><Eye size={16} /> View certificate</Btn>}
        </Card>
      ) : (
        <Card data-testid="my-lso-empty">
          <p className="font-bold">No certificate on file yet</p>
          <p className="mt-1 text-sm text-neutral-400">Complete ALR's LSO Training and your certificate will appear here automatically. You can also upload a certificate you earned elsewhere.</p>
        </Card>
      ))}
      <Btn onClick={() => navigate("/lso")} data-testid="my-lso-take-training"><GraduationCap size={16} /> {latest ? "Retake LSO Training" : "Take LSO Training"}</Btn>
      <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.pdf" hidden onChange={upload} data-testid="my-lso-file-input" />
      <Btn variant="outline" busy={busy} onClick={() => fileRef.current?.click()} data-testid="upload-my-lso-cert"><CloudUpload size={16} /> {latest ? "Upload a new certificate" : "Upload my certificate"}</Btn>
    </PortalPage>
  );
}
